import React from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';
import { trackEvent } from '../utils/analytics';

const Home = () => {
  usePageMeta({
    title: 'Monthly Wealth Tracker - Track Your Net Worth & Financial Growth',
    description: 'Free personal finance tracker to monitor your assets, debts, and net worth over time. Beautiful charts, secure cloud sync, and Google sign-in. Start tracking your wealth journey today!',
    path: '/',
  });

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 py-5 max-w-5xl mx-auto">
        <div className="flex items-center gap-2.5">
          <img src="/logo.png" alt="" className="w-7 h-7 rounded-lg" />
          <span className="font-semibold text-sm">Monthly Wealth Tracker</span>
        </div>
        <Link
          to="/app"
          onClick={() => trackEvent('cta_clicked', { cta: 'nav_open_app' })}
          className="text-sm text-accent hover:text-emerald-700 transition-colors font-medium"
        >
          Open App →
        </Link>
      </nav>

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-6 py-24 sm:py-32">
        <h1 className="text-4xl sm:text-6xl font-bold text-gray-900 leading-[1.1] tracking-tight mb-6">
          Track your net worth.<br />
          <span className="text-accent">Month by month.</span>
        </h1>
        <p className="text-gray-500 text-lg max-w-xl mb-10 leading-relaxed">
          A minimal tool for recording your assets and debts monthly. No bank connections, no complexity — just clear data about where you stand financially.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link
            to="/app"
            onClick={() => trackEvent('cta_clicked', { cta: 'hero_get_started' })}
            className="inline-flex items-center justify-center px-6 py-3 bg-accent text-white rounded-xl font-medium text-sm hover:bg-emerald-700 transition-colors shadow-sm"
          >
            Get Started Free
          </Link>
          <a
            href="#how"
            onClick={() => trackEvent('cta_clicked', { cta: 'how_it_works' })}
            className="inline-flex items-center justify-center px-6 py-3 border border-gray-200 rounded-xl text-gray-600 text-sm hover:text-gray-900 hover:bg-gray-50 transition-colors"
          >
            How it works
          </a>
        </div>

        <div className="mt-16 rounded-2xl border border-gray-200 shadow-card overflow-hidden">
          <img
            src="/dashboard-preview.png"
            alt="Monthly Wealth Tracker dashboard showing a year of net worth growth and FIRE progress"
            className="w-full h-auto block"
            width="1440"
            height="1060"
          />
        </div>
      </div>

      {/* Features */}
      <div id="how" className="max-w-5xl mx-auto px-6 py-20 border-t border-gray-100">
        <h2 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-12">How it works</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { step: '01', title: 'Add accounts', body: 'Create named accounts for your assets — savings, investments, property — and your debts — loans, credit cards.' },
            { step: '02', title: 'Enter monthly balances', body: 'Once a month, enter the current balance for each account. Takes under two minutes.' },
            { step: '03', title: 'Watch your progress', body: 'Charts and history show exactly how your net worth is changing over time.' },
          ].map(({ step, title, body }) => (
            <div key={step} className="bg-white rounded-2xl border border-gray-100 shadow-card p-8">
              <p className="text-accent text-xs font-semibold mb-4">{step}</p>
              <h3 className="text-gray-900 font-semibold tracking-tight mb-2">{title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Storage */}
      <div className="max-w-5xl mx-auto px-6 py-20 border-t border-gray-100">
        <h2 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-12">Your data, your way</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            {
              label: 'Cloud Mode',
              desc: 'Sign in with Google. Access from any device, automatic backups, never lose your data.',
              tag: 'multi-device',
            },
            {
              label: 'Offline Mode',
              desc: 'No account required. Data stays in your browser. Complete privacy.',
              tag: 'privacy-first',
            },
          ].map(({ label, desc, tag }) => (
            <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-card p-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-gray-900 font-semibold tracking-tight">{label}</h3>
                <span className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-2.5 py-0.5">{tag}</span>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-5xl mx-auto px-6 py-20 border-t border-gray-100 text-center">
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight mb-4">Start tracking today</h2>
        <p className="text-gray-500 text-sm mb-8">Free. No credit card. No bank connections required.</p>
        <Link
          to="/app"
          onClick={() => trackEvent('cta_clicked', { cta: 'bottom_open_app' })}
          className="inline-flex items-center justify-center px-8 py-3 bg-accent text-white rounded-xl font-medium text-sm hover:bg-emerald-700 transition-colors shadow-sm"
        >
          Open App
        </Link>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-100 px-6 py-8">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-gray-400">
          <span>© 2025 Monthly Wealth Tracker</span>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-gray-600 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-gray-600 transition-colors">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
